import { useMemo, useState } from 'react';
import { useAutomaton } from '../state/AutomatonContext';
import { LeaderboardHeader, LeaderboardRow, fmtPrimary } from '../components/LeaderboardRow';
import { cellIsAlive } from '../lib/survival';

type Tab = 'rich' | 'earn' | 'survive' | 'blood';

const TABS: { id: Tab; label: string }[] = [
  { id: 'rich', label: 'Richest' },
  { id: 'earn', label: 'Top Earners' },
  { id: 'survive', label: 'Survivors' },
  { id: 'blood', label: 'Bloodlines' },
];

export function Leaderboard() {
  const { cells } = useAutomaton();
  const [tab, setTab] = useState<Tab>('rich');
  const [page, setPage] = useState(0);
  const pageSize = 25;

  const ordered = useMemo(() => {
    const alive = cells.filter((c) => cellIsAlive(c) && c.agent);
    switch (tab) {
      case 'rich':
        return [...alive].sort((a, b) => (b.agent!.balance - a.agent!.balance));
      case 'earn':
        return [...alive].sort((a, b) => (b.agent!.revenue24h - a.agent!.revenue24h));
      case 'survive':
        return [...alive].sort((a, b) => (a.agent!.bornAt - b.agent!.bornAt));
      case 'blood':
        return [...alive].sort((a, b) => (b.agent!.childrenIds.length - a.agent!.childrenIds.length));
    }
  }, [tab, cells]);

  const totalPages = Math.max(1, Math.ceil(ordered.length / pageSize));
  const slice = ordered.slice(page * pageSize, (page + 1) * pageSize);

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div className="space-y-2">
        <div className="text-blue-life text-xs font-mono uppercase tracking-tightest">/leaderboard</div>
        <h1 className="font-display font-semibold tracking-tightest text-xl">
          The colony's apex.
        </h1>
      </div>

      <nav className="flex gap-1 border-b border-grid-line sticky top-14 bg-void/80 backdrop-blur z-20 sm:static sm:bg-transparent sm:backdrop-blur-none">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => { setTab(t.id); setPage(0); }}
            className={`px-3 py-2 text-xs uppercase tracking-tightest ${
              tab === t.id ? 'text-blue-life border-b-2 border-blue-life' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <LeaderboardHeader />

      <div className="space-y-1">
        {slice.map((cell, i) => {
          const { value, label } = fmtPrimary(cell, tab);
          return (
            <LeaderboardRow
              key={cell.id}
              rank={page * pageSize + i + 1}
              cell={cell}
              primaryStat={value}
              primaryLabel={label}
            />
          );
        })}
      </div>

      <div className="flex items-center justify-between text-2xs font-mono text-text-secondary">
        <button
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          className="hairline px-3 py-1 disabled:opacity-30"
        >
          ← Prev
        </button>
        <div>
          Page {page + 1} / {totalPages}
        </div>
        <button
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={page >= totalPages - 1}
          className="hairline px-3 py-1 disabled:opacity-30"
        >
          Next →
        </button>
      </div>
    </div>
  );
}
